import { Col, Row, Statistic, Card, Table, Tag, App } from 'antd'
import {
  FileTextOutlined, SendOutlined, ToolOutlined,
  FileOutlined, DollarOutlined, AlertOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { purchaseRequisitionsApi, rfqApi, workOrdersApi, purchaseInvoicesApi, rejectionNotesApi } from '@/features/procurement/api'
import PageHeader from '@/shared/components/PageHeader'

const STATUS_COLOR: Record<string, string> = {
  DRAFT: 'default', PENDING_APPROVAL: 'orange', APPROVED: 'green',
  REJECTED: 'red', OPEN: 'blue', CLOSED: 'cyan', IN_PROGRESS: 'processing',
  COMPLETED: 'success', CANCELLED: 'error', PENDING: 'warning',
  '3WAY_MATCHED': 'purple', DISPUTED: 'red',
}

export default function ProcurementDashboardPage() {
  const { data: prs } = useQuery({ queryKey: ['prs-all'], queryFn: () => purchaseRequisitionsApi.list({ limit: 500 }) })
  const { data: rfqs } = useQuery({ queryKey: ['rfqs-all'], queryFn: () => rfqApi.list({ limit: 500 }) })
  const { data: wos } = useQuery({ queryKey: ['wos-all'], queryFn: () => workOrdersApi.list({ limit: 500 }) })
  const { data: invoices } = useQuery({ queryKey: ['invoices-all'], queryFn: () => purchaseInvoicesApi.list({ limit: 500 }) })
  const { data: rns } = useQuery({ queryKey: ['rns-all'], queryFn: () => rejectionNotesApi.list({ limit: 500 }) })

  const openPRs = prs?.filter(p => p.status === 'PENDING_APPROVAL').length ?? 0
  const openRFQs = rfqs?.filter(r => r.status === 'OPEN').length ?? 0
  const activeWOs = wos?.filter(w => ['APPROVED', 'IN_PROGRESS'].includes(w.status)).length ?? 0
  const pendingInvoices = invoices?.filter(i => i.status === 'PENDING').length ?? 0
  const pendingRNs = rns?.filter(r => r.rtv_status === 'PENDING').length ?? 0

  const recentPRs = (prs ?? []).slice(0, 5)
  const recentInvoices = (invoices ?? []).slice(0, 5)

  return (
    <div>
      <PageHeader title="Purchase Dashboard" subtitle="Overview of procurement activities" />

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic title="PRs Pending Approval" value={openPRs} prefix={<FileTextOutlined />} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic title="Open RFQs" value={openRFQs} prefix={<SendOutlined />} valueStyle={{ color: '#1677ff' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic title="Active Work Orders" value={activeWOs} prefix={<ToolOutlined />} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic title="Invoices to Match" value={pendingInvoices} prefix={<DollarOutlined />} valueStyle={{ color: '#722ed1' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic title="Pending RTVs" value={pendingRNs} prefix={<AlertOutlined />} valueStyle={{ color: '#f5222d' }} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Recent Purchase Requisitions">
            <Table
              rowKey="id"
              dataSource={recentPRs}
              pagination={false}
              size="small"
              columns={[
                { title: 'PR No', dataIndex: 'pr_no', key: 'pr_no' },
                { title: 'Type', dataIndex: 'pr_type', key: 'pr_type' },
                { title: 'Plant', dataIndex: 'plant_name', key: 'plant_name' },
                {
                  title: 'Status', dataIndex: 'status', key: 'status',
                  render: (v: string) => <Tag color={STATUS_COLOR[v] ?? 'default'}>{v}</Tag>,
                },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Recent Invoices">
            <Table
              rowKey="id"
              dataSource={recentInvoices}
              pagination={false}
              size="small"
              columns={[
                { title: 'Invoice No', dataIndex: 'inv_no', key: 'inv_no' },
                { title: 'Supplier', dataIndex: 'supplier_name', key: 'supplier_name' },
                { title: 'Net Payable', dataIndex: 'net_payable', key: 'net_payable' },
                {
                  title: 'Status', dataIndex: 'status', key: 'status',
                  render: (v: string) => <Tag color={STATUS_COLOR[v] ?? 'default'}>{v}</Tag>,
                },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </div>
  )
}
